import { DrawerProps, Drawer, Descriptions, Typography } from "antd";
import SVGIcon from "../../../../utils/SVGIcon";
import Config from "../../../../config/Config";
import CancelButton from "../../../../components/Buttons/CancelButton";
import { useAppSelector } from "../../../../utils/hooks/hooks";
import { renderNA } from "../../../../utils/commonFunctions";
import { genderOptions } from "../../../../config/Constant";
import dayjs from "dayjs";
import { dateFormats } from "../utils/constats";
import { RootState } from "../../../../store/app";
import { dateFormatter } from "../../../../config/Global";
const { Title } = Typography;

interface ViewDrawerProps extends DrawerProps {
  titleName: string;
  open: boolean;
  close: () => void;
}

/**
 * Renders a view drawer component for displaying user details.
 *
 * @param {ViewDrawerProps} props - The component props.
 * @param {string} props.titleName - The title of the drawer.
 * @param {boolean} props.open - Whether the drawer is open or not.
 * @param {function} props.close - The function to close the drawer.
 * @param {...rest} props.rest - Additional props for the component.
 * @return {JSX.Element} The rendered view drawer component.
 */
const ViewDrawer: React.FC<ViewDrawerProps> = ({
  titleName,
  open,
  close,
  ...rest
}) => {
  var details = useAppSelector((state) => state.user.details);
  const drawerClose = () => {
    close();
  };

   const InitData: any = useAppSelector(
      (state: RootState) => state.auth.initialData
    );

  return (
    <Drawer
      title={`View ${titleName}`}
      open={open}
      placement="right"
      width={"70%"}
      onClose={drawerClose}
      destroyOnClose
      closeIcon={<SVGIcon icon="close" color={Config.themePrimaryBtn} />}
      footer={[<CancelButton key={2} onClick={drawerClose} />]}
      {...rest}
    >
      <Descriptions column={2} bordered layout="horizontal">
        <Descriptions.Item label="First Name">
          {renderNA(details?.name)}
        </Descriptions.Item>
        <Descriptions.Item label="Last Name">
          {renderNA(details?.last_name)}
        </Descriptions.Item>
        <Descriptions.Item label="Mobile Number">
          {renderNA(details?.mobile_no)}
        </Descriptions.Item>
        <Descriptions.Item label="Email">
          {renderNA(details?.email)}
        </Descriptions.Item>
        <Descriptions.Item label="Role">
          {renderNA(details?.role?.name)}
        </Descriptions.Item>
        {details?.company && details?.company?.id &&
          <Descriptions.Item label="Customer Name">
            {renderNA(details?.company?.company_name)}
          </Descriptions.Item>
        }
        {details?.role?.id == '2' && details?.campaign && details?.campaign?.campaign_name &&
          <Descriptions.Item label="Subscription Name">
            {renderNA(details?.campaign?.campaign_name)}
          </Descriptions.Item>
        }
        {details?.role?.id == '2' && details?.access_code != undefined &&
          <Descriptions.Item label="Access Code">
            <Title level={5} type="secondary" code>{renderNA(details.access_code)}</Title>
          </Descriptions.Item>
        }
        {![3, 1].includes(details?.role?.id) && details?.industry && details?.industry?.id &&
          <Descriptions.Item label="Industry Name">
            {renderNA(details?.industry?.industry_name)}
          </Descriptions.Item>
        }
        {details?.role?.id == '2' && details?.is_invite != undefined &&
          <Descriptions.Item label="Invitation Status">
            {renderNA(details.is_invite == 1 ? 'Invited' : 'Registered')}
          </Descriptions.Item>
        }
        {![1, 3].includes(details?.role?.id) && (
          <>
            <Descriptions.Item label="Date of Birth">
              {details?.dob
                ? dayjs(details?.dob).format(dateFormats.dob)
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Gender">
              {renderNA(
                InitData?.gender.find(
                  (option: any) => option.id === details?.gender
                )?.name
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Disability Status">
              {renderNA(details?.disabilities?.name)}
            </Descriptions.Item>
            <Descriptions.Item label="Ethnicity">
              {renderNA(details?.race?.name)}
            </Descriptions.Item>
            <Descriptions.Item label="Military Status">
              {renderNA(details?.veteran?.name)}
            </Descriptions.Item>
            <Descriptions.Item label="Household Income">
              {renderNA(details?.house_hold_income?.name)}
            </Descriptions.Item>
            <Descriptions.Item label="Location (Zip Code)">
              {renderNA(details?.location)}
            </Descriptions.Item>
            <Descriptions.Item label="City">
              {renderNA(details?.city)}
            </Descriptions.Item>
            <Descriptions.Item label="Willing to Relocate?">
              {details?.willing_to_relocate != null ? details?.willing_to_relocate == 1 ? 'Yes' : 'No' : renderNA(details?.willing_to_relocate)}
            </Descriptions.Item>
            <Descriptions.Item label="Education Level">
              {renderNA(details?.education?.name)}
            </Descriptions.Item>
            <Descriptions.Item label="Years of Experience">
              {renderNA(details?.experience?.name)}
            </Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="Last Login">
          {renderNA(details?.last_login && dateFormatter(details?.last_login, dateFormats.dateTimeFormat))}
        </Descriptions.Item>
      </Descriptions>
    </Drawer>
  );
};

export default ViewDrawer;
